import React, { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, X, Loader2, Download, ThumbsUp, ThumbsDown, Play, Lightbulb, PencilLine } from "lucide-react";
import { MyClipsByProjectIdQuery, MyClipQuery, MyClipQueryVariables } from "@/generated/graphql";
import { useMutation, useLazyQuery } from "@apollo/client";
import { RATE_CLIP, GET_MY_CLIP } from "@/queries/queries";
import { ClipTitlesDialog } from "@/components/ClipTitlesDialog";
import { useClipPolling } from "@/hooks/use-polling";
import { getClipStatusText } from "@/lib/get-clip-status-text";
import { formatDuration } from "@/lib/format";
import { downloadClipVideo } from "@/lib/downloads/clip-download";
import { useToast } from "@/hooks/use-toast";
import ShareClipButton from "@/components/ShareClipButton";
import { ClipPublishingControls } from "@/components/publishing/ClipPublishingControls";
import { EditWithAiButton } from "@/components/assistant/EditWithAiButton";
import { canMakeQuoteCarousel } from "@/lib/quoteCarousel/canMakeQuoteCarousel";
import { PublishStatusOverlay } from "@/components/publishing/PublishStatusOverlay";
import type { PublishMediaKinds, PublishUiStatus } from "@/lib/publishing/types";
import { useDownloadUpsell } from "@/contexts/downloadUpsellContext";

export interface HorizontalClipCardProps {
  clip: MyClipsByProjectIdQuery['myClips'][0];
  clipNumber: number;
  isSelected: boolean;
  onToggleSelection: (clipId: string) => void;
  videoUrl?: string;
  onEditVideo?: (clipId: string) => void;
  /** Opens the clip in AI Studio (shown only when the page passes it). */
  onEditWithAi?: (clipId: string) => void;
  onSendToQueue?: (clipId: string) => void;
  onSendToTikTok?: (clipId: string) => void;
  onPublishToYouTube?: (clipId: string) => void;
  onPublishToInstagram?: (clipId: string) => void;
  onPublishToFacebook?: (clipId: string) => void;
  tiktokPublishStatus?: PublishUiStatus;
  tiktokPublishFailReason?: string | null;
  youtubePublishStatus?: PublishUiStatus;
  youtubePublishFailReason?: string | null;
  instagramPublishStatus?: PublishUiStatus;
  instagramPublishFailReason?: string | null;
  facebookPublishStatus?: PublishUiStatus;
  facebookPublishFailReason?: string | null;
  /** Latest publish media_kind per platform ('carousel' adds an icon). */
  publishMediaKinds?: PublishMediaKinds;
}

const PENDING_STATUSES = ['processing', 'pre-queued', 'queued'];

export const HorizontalClipCard: React.FC<HorizontalClipCardProps> = ({
  clip,
  clipNumber,
  isSelected,
  onToggleSelection,
  videoUrl,
  onEditVideo,
  onEditWithAi,
  onSendToQueue,
  onSendToTikTok,
  onPublishToYouTube,
  onPublishToInstagram,
  onPublishToFacebook,
  tiktokPublishStatus,
  tiktokPublishFailReason,
  youtubePublishStatus,
  youtubePublishFailReason,
  instagramPublishStatus,
  instagramPublishFailReason,
  facebookPublishStatus,
  facebookPublishFailReason,
  publishMediaKinds,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { notifyClipDownloaded } = useDownloadUpsell();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPlayerMounted, setIsPlayerMounted] = useState(false);
  const [clipRating, setClipRating] = useState<string>(clip.curation_rating || 'neutral');
  const [titlesDialogOpen, setTitlesDialogOpen] = useState(false);
  const [rateClipMutation] = useMutation(RATE_CLIP);
  const [fetchFreshClip, { data: freshClipData }] = useLazyQuery<MyClipQuery, MyClipQueryVariables>(GET_MY_CLIP, { fetchPolicy: 'network-only' });

  const { data: polledClip } = useClipPolling(
    clip.id,
    {
      enabled: (
        PENDING_STATUSES.includes(clip.clipping_status) ||
        PENDING_STATUSES.includes(clip.preparing_status) ||
        PENDING_STATUSES.includes(clip.rendering_status)
      ),
      interval: 5000,
    }
  );

  const currentClip = polledClip || clip;

  const isCurrentStateProcessing = useMemo(() => {
    const c = polledClip || clip;
    return PENDING_STATUSES.includes(c.rendering_status) || PENDING_STATUSES.includes(c.preparing_status) || PENDING_STATUSES.includes(c.clipping_status);
  }, [clip, polledClip]);

  const isCurrentStateFailure = useMemo(() => {
    const c = polledClip || clip;
    return c.rendering_status === 'failure' || c.clipping_status === 'failure';
  }, [clip, polledClip]);

  const isExpired = useMemo(() => {
    const c = polledClip || clip;
    return c.rendering_status === 'expired'
      || c.preparing_status === 'expired'
      || c.clipping_status === 'expired';
  }, [clip, polledClip]);

  const effectiveStatusLabel = getClipStatusText(currentClip as any);

  const effectiveDownloadUrl = currentClip.finished_video_url || (currentClip as any).compressed_video_url || (currentClip as any).clipped_video_url;
  const thumbnailUrl = currentClip.finished_video_thumbnail_url || currentClip.thumbnail_url || (currentClip as any).clip_thumbnail;
  const isRendered = currentClip.rendering_status === 'success' && !!effectiveDownloadUrl;
  const isCurrentStateSuccess = isRendered;
  const isSelectionDisabled = isCurrentStateProcessing || isCurrentStateFailure || isExpired;

  const canPlay = (!!videoUrl || isRendered) && !isCurrentStateProcessing && !isCurrentStateFailure && !isExpired;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!effectiveDownloadUrl || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadClipVideo({
        clipId: currentClip.id,
        fallbackName: currentClip.name || `clip_${clipNumber}`,
        downloadUrl: effectiveDownloadUrl,
      });
      notifyClipDownloaded();
    } catch {
      toast({ title: t('projectClips.toast.downloadError'), variant: "destructive" });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRating = async (e: React.MouseEvent, rating: string) => {
    e.stopPropagation();
    const newRating = clipRating === rating ? 'neutral' : rating;
    setClipRating(newRating);
    try {
      await rateClipMutation({ variables: { id: clip.id, rating: newRating } });
    } catch {
      setClipRating(clipRating);
    }
  };

  return (
    <>
    <Card
      className={`min-w-0 overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-[1.02] group ${
        isSelected ? 'ring-2 ring-primary shadow-[0_0_20px_-5px] shadow-primary/30' : ''
      } ${isSelectionDisabled ? 'opacity-60' : ''} ${!isSelectionDisabled ? 'cursor-pointer' : 'cursor-not-allowed'}`}
      onClick={() => !isSelectionDisabled && onToggleSelection(clip.id)}
    >
      <div className="relative aspect-video bg-black">
        {isPlayerMounted && isRendered ? (
          <video
            ref={videoRef}
            src={effectiveDownloadUrl}
            controls
            playsInline
            className="w-full h-full object-contain"
            autoPlay
          />
        ) : thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={currentClip.name || t('verticalClipCard.clipNumber', { number: clipNumber })}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <span className="text-muted-foreground text-sm">{t('verticalClipCard.noPreview')}</span>
          </div>
        )}

        {/* Play button */}
        {!isPlayerMounted && canPlay && isRendered && (
          <button
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[5] w-11 h-11 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/15 transition-all duration-200 hover:bg-primary/80 hover:scale-110 hover:border-primary/40 shadow-[0_2px_16px_rgba(0,0,0,0.4)]"
            onClick={(e) => { e.stopPropagation(); setIsPlayerMounted(true); }}
          >
            <Play className="w-5 h-5 text-white fill-white ml-0.5" />
          </button>
        )}

        {/* Duration badge */}
        <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-[11px] font-medium px-2 py-0.5 rounded-full tracking-wide pointer-events-none">
          {formatDuration(currentClip.duration_seconds)}
        </div>

        {/* Selection checkbox */}
        {!isSelectionDisabled && (
          <div
            className="absolute top-1 left-1 z-10"
            onClick={(e) => { e.stopPropagation(); onToggleSelection(clip.id); }}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-primary border-primary text-primary-foreground' : 'bg-background/80 border-muted-foreground/40 hover:border-primary'}`}>
              {isSelected && <Check className="w-3 h-3" />}
            </div>
          </div>
        )}
        {isRendered && !isExpired && (
        <PublishStatusOverlay
          className={`absolute z-20 ${isSelectionDisabled ? 'top-2 left-2' : 'top-1 left-8'}`}
          statuses={[
            {
              platform: 'tiktok',
              status: tiktokPublishStatus,
              failReason: tiktokPublishFailReason,
              mediaKind: publishMediaKinds?.tiktok,
            },
            {
              platform: 'instagram',
              status: instagramPublishStatus,
              failReason: instagramPublishFailReason,
              mediaKind: publishMediaKinds?.instagram,
            },
            {
              platform: 'facebook',
              status: facebookPublishStatus,
              failReason: facebookPublishFailReason,
              mediaKind: publishMediaKinds?.facebook,
            },
            {
              platform: 'youtube',
              status: youtubePublishStatus,
              failReason: youtubePublishFailReason,
              mediaKind: publishMediaKinds?.youtube,
            },
          ]}
        />
        )}

        {/* Processing overlay */}
        {isCurrentStateProcessing && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-white mx-auto mb-2" />
              <span className="text-white text-xs">{effectiveStatusLabel}</span>
            </div>
          </div>
        )}

        {isExpired && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="text-center">
              <X className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <span className="text-amber-500 text-xs">{effectiveStatusLabel}</span>
            </div>
          </div>
        )}

        {isCurrentStateFailure && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="text-center">
              <X className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <span className="text-red-500 text-xs">{effectiveStatusLabel}</span>
            </div>
          </div>
        )}

        {/* Ready indicator */}
        {isCurrentStateSuccess && !isCurrentStateProcessing && (
          <div className="absolute bottom-2 left-2 bg-emerald-500/20 backdrop-blur-sm text-emerald-400 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 pointer-events-none border border-emerald-500/20">
            <Check className="w-3 h-3" />
            {t('verticalClipCard.ready')}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="min-w-0 p-2.5 space-y-2">
        <h3 className="font-semibold text-sm leading-tight truncate" title={currentClip.name || undefined}>
          {currentClip.name || t('verticalClipCard.clipNumber', { number: clipNumber })}
        </h3>

        {/* Score + rating */}
        <div className="flex items-center gap-2">
          {currentClip.clip_score != null && (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span>Score:</span>
              <div className="w-8 h-1 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-gradient-primary rounded-full" style={{ width: `${Math.round((currentClip.clip_score ?? 0) * 100)}%` }} />
              </div>
              <span className="font-medium">{Math.round((currentClip.clip_score ?? 0) * 100)}</span>
            </div>
          )}
          <div className="flex items-center gap-1 ml-auto">
            <button
              className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${clipRating === 'like' ? 'bg-green-500 text-white' : 'bg-muted/60 text-muted-foreground hover:text-green-500'}`}
              onClick={(e) => handleRating(e, 'like')}
            >
              <ThumbsUp className="w-3 h-3" />
            </button>
            <button
              className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${clipRating === 'dislike' ? 'bg-red-500 text-white' : 'bg-muted/60 text-muted-foreground hover:text-red-500'}`}
              onClick={(e) => handleRating(e, 'dislike')}
            >
              <ThumbsDown className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Download + Share + Copy */}
        <div className="flex gap-1.5">
          {effectiveDownloadUrl && (
            <>
              <Button
                variant="gradient"
                size="sm"
                onClick={handleDownload}
                disabled={isDownloading}
                className="flex-1 h-8 text-xs"
              >
                {isDownloading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                {t('verticalClipCard.download')}
              </Button>
              <ShareClipButton
                downloadUrl={effectiveDownloadUrl!}
                clipName={currentClip.name || t('verticalClipCard.clipNumber', { number: clipNumber })}
              />
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              fetchFreshClip({ variables: { id: clip.id } });
              setTitlesDialogOpen(true);
            }}
          >
            <Lightbulb className="w-3.5 h-3.5 text-green-500" />
          </Button>
          {onEditVideo && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e) => {
                e.stopPropagation();
                onEditVideo(clip.id);
              }}
              title={t('projectClips.editVideo')}
            >
              <PencilLine className="w-3.5 h-3.5" />
            </Button>
          )}
          {onEditWithAi && (
            <EditWithAiButton onClick={() => onEditWithAi(clip.id)} />
          )}
        </div>
        <ClipPublishingControls
          clipId={clip.id}
          canPublish={isRendered && !isExpired}
          canMakeCarousel={canMakeQuoteCarousel(currentClip)}
          compact
          onSendToQueue={onSendToQueue}
          onSendToTikTok={onSendToTikTok}
          onPublishToYouTube={onPublishToYouTube}
          onPublishToInstagram={onPublishToInstagram}
          onPublishToFacebook={onPublishToFacebook}
          tiktokPublishStatus={tiktokPublishStatus}
          tiktokPublishFailReason={tiktokPublishFailReason}
          youtubePublishStatus={youtubePublishStatus}
          youtubePublishFailReason={youtubePublishFailReason}
          instagramPublishStatus={instagramPublishStatus}
          instagramPublishFailReason={instagramPublishFailReason}
          facebookPublishStatus={facebookPublishStatus}
          facebookPublishFailReason={facebookPublishFailReason}
        />
      </div>
    </Card>

    <ClipTitlesDialog
      open={titlesDialogOpen}
      onOpenChange={setTitlesDialogOpen}
      clipId={clip.id}
      clipName={currentClip.name || t('verticalClipCard.clipNumber', { number: clipNumber })}
      existingCopy={freshClipData?.myClip?.copy}
    />
    </>
  );
};

export default HorizontalClipCard;
